import PageTitleBlock from '@organisms/PageTitleBlock';
import {
  CompareContentTitleRow,
  CompareContentWrapper,
  ComparePageWrapper,
  CompareContentBtnsWrapper,
  CompareSubsystemTablesWrapper,
} from '@pages/ComparePage.styles';
import {useParams} from 'react-router';
import {useDispatch, useSelector} from 'react-redux';
import {systems} from '@store/slices/systems';
import Button from '@components/atoms/Button';
import {CalendarAddAlt, Export} from '@carbon/icons-react';
import {SystemHistory} from '@organisms/SystemHistory';
import Text from '@atoms/Text';
import SubsystemCompareBlock from '@organisms/SubsystemCompareBlock';
import {CoverageChanges} from '@components/atoms/CoverageChanges';
import {ISnapshot, ISystem} from '@types';
import ReportExportModal from '@organisms/modals/ReportExportModal';
import {useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router';
import {compareState, getSystemSnapshotsRangeThunk} from '@store/slices/compare';
import {getPlans} from '@store/slices/coveragePlans';
import {AppDispatch} from '@store';

export const ComparePage = () => {
  const {id} = useParams<{id: string}>();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const systemsArr: ISystem[] = useSelector(systems);
  const currentSystem = systemsArr.find(system => String(system.id) === id);
  const [showReportExport, setShowReportExport] = useState(false);
  const [beforeSnapshotID, setBeforeSnapshotID] = useState<string | null>(null);
  const [afterSnapshotID, setAfterSnapshotID] = useState<string | null>(null);
  const {data: compareData} = useSelector(compareState);

  const range = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const leftStr = params.get('left');
    const rightStr = params.get('right');
    const toDate = (iso: string): Date | null => {
      const m = iso?.match?.(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return null;
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      d.setHours(0, 0, 0, 0);
      return d;
    };
    const left = leftStr ? toDate(leftStr) : null;
    const right = rightStr ? toDate(rightStr) : null;
    return left && right ? {left, right} : null;
  }, [location.search]);

  const snapshotToRender: ISnapshot | undefined = useMemo(() => {
    if (compareData && Array.isArray(compareData.subsystems)) {
      return compareData as ISnapshot;
    }
    return currentSystem?.snapshot as ISnapshot | undefined;
  }, [compareData, currentSystem]);

  useEffect(() => {
    if (id) {
      dispatch(getPlans(Number(id)));
    }
  }, [id, dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const before_snapshot_id = params.get('before_snapshot_id');
    const after_snapshot_id = params.get('after_snapshot_id');
    setBeforeSnapshotID(before_snapshot_id);
    setAfterSnapshotID(after_snapshot_id);
    if (before_snapshot_id && after_snapshot_id && id) {
      dispatch(
        getSystemSnapshotsRangeThunk({
          system_id: id,
          before_snapshot_id,
          after_snapshot_id,
        }) as any,
      );
    }
  }, [location.search, id, dispatch]);

  return (
    <ComparePageWrapper>
      <PageTitleBlock
        title={currentSystem?.name}
        buttons={[
          <Button key={'add-subsystem-title'} title="Добавить подсистему" disabled />,
          <Button
            key={'generate-report'}
            title="Сформировать отчёт"
            iconElement={<Export />}
            onClick={() => setShowReportExport(true)}
          />,
        ]}
        withBackBtn
        backBtnText="Вернуться к актуальным данным"
        backBtnLink={`/systems/${id}`}
        systems={systemsArr}
      />
      <CompareContentWrapper>
        <CompareContentTitleRow>
          <Text text="Сравнение общего покрытия системы" type="captionB" />
          <CoverageChanges
            coverage={
              snapshotToRender
                ? (snapshotToRender.general_auto_coverage ?? 0) +
                  (snapshotToRender.general_coverage ?? 0)
                : (currentSystem?.auto_coverage ?? 0) +
                  (currentSystem?.test_coverage ?? 0)
            }
            changes={
              snapshotToRender
                ? (snapshotToRender.general_auto_coverage_delta ?? 0) +
                  (snapshotToRender.general_coverage_delta ?? 0)
                : 0
            }
          />
          <CompareContentBtnsWrapper>
            <SystemHistory />
            <Button
              title="Планирование"
              size="small"
              iconElement={<CalendarAddAlt />}
              iconPosition="left"
              disabled
            />
          </CompareContentBtnsWrapper>
        </CompareContentTitleRow>
        <CompareSubsystemTablesWrapper>
          {snapshotToRender?.subsystems.map(subsystem => {
            return (
              <SubsystemCompareBlock
                key={`subsystem-details-block-${subsystem.id}`}
                subsystem={subsystem}
              />
            );
          })}
        </CompareSubsystemTablesWrapper>
      </CompareContentWrapper>
      <ReportExportModal
        isOpened={showReportExport}
        onCloseRequest={() => setShowReportExport(false)}
        systemName={currentSystem?.name}
        systemID={currentSystem?.id}
        subsystems={snapshotToRender?.subsystems}
        leftDate={range?.left ?? undefined}
        rightDate={range?.right ?? undefined}
        beforeSnapshotID={Number(beforeSnapshotID)}
        afterSnapshotID={Number(afterSnapshotID)}
      />
    </ComparePageWrapper>
  );
};

export default ComparePage;
